import programApi from "@api/programs" //eslint-disable-line
import Vue from 'vue'
import { errorPromise } from '@util/ErrorHandling.js'

export default {
    namespaced: true,
    state: {
        isSubmitting: false,
        loading: {
            basicInfoSave: false,
            programList: false,
            program: false,
            programSave: false,
            programStatusSave: false,
            locations: false,
        },
        errors: {
            basicInfoSave: false,
            programList: null,
            program: null,
            programSave: null,
            programStatusSave: null,
        },
        programList: [],
        programOwners: [],
        program: {
            client_uuid: null,
            product_uuid: null,
            programowner_uuid: null,
            name: '',
            status: 'active',
            type: 'CPC',
            max_monthly_budget: null,
            notify_on_met_budget: false,
            email_weekly_report: true,
            categories: [],
            targeting_sets: [],
        },
        countries: [],
        locations: {
            countries: [],
            states: [],
            presets: [],
        },
        targetingSetHistory: [],
        programHistory: [],
        wizardStartOver: true,
    },
    getters: {
        wizardStartOver: ({ wizardStartOver }) => wizardStartOver,
        areBudgetsComplete: ({ program }) => true, // eslint-disable-line
        presets: ({ locations }) => locations.presets,
        program: ({ program }) => program,
        targetingSetHistory: state => state.targetingSetHistory,
        programList: state => state.programList,
        getCurrentProgramOwnerName(state) {
            const selectedProgramOwner = state.programOwners.find(
                programOwner => programOwner.uuid === state.program.programowner_uuid,
            )
            return selectedProgramOwner != null ? selectedProgramOwner.display_name : ''
        },
        getProgramOwnerName: state => programownerUuid => {
            const selectedProgramOwner = state.programOwners.find(
                programOwner => programOwner.uuid === programownerUuid,
            )
            return selectedProgramOwner != null ? selectedProgramOwner.display_name : ''
        },
        programOwners: state => state.programOwners,
        getLoadingState: state => name => state.loading[name],
        getErrorState: state => name => state.errors[name],
        geoCountries: state => state.countries,
        geoLocations: state => state.locations,
        availableCountries: state => targetingSet => {
            const category = state.program.categories.find(cat => cat.uuid === targetingSet.category_uuid)
            let countriesInUse = []
            if (category) {
                category.targetingSets.forEach(set => {
                    if (!set.is_active || set.deleted_at) return // if set is inactive, its countries aren't in use
                    countriesInUse = countriesInUse.concat(set.countries)
                })
            }
            return state.locations.countries.filter(
                country => !countriesInUse.find(countryInUse => countryInUse.country_uuid === country.uuid)
                    || targetingSet.countries.find(
                        targetingSetCountry => targetingSetCountry.country_uuid === country.uuid,
                    ),
            )
        },
        otherTargetingSetsCountriesInUse: state => targetingSet => {
            const category = state.program.categories.find(cat => cat.uuid === targetingSet.category_uuid)
            let countriesInUse = []
            if (category) {
                category.targetingSets.forEach(set => {
                    // if set is inactive, its countries aren't in use
                    if (set.uuid === targetingSet.uuid || !set.is_active || set.deleted_at) return
                    countriesInUse = countriesInUse.concat(set.countries)
                })
            }
            return countriesInUse
        },
        stepStatuses(state) {
            return {
                program: {
                    started: true,
                    complete: !!state.program.name
                        && !!state.program.product_uuid
                        && !!state.program.programowner_uuid
                        && !!state.program.type,
                },
                targeting: {
                    started: state.program.max_monthly_budget > 0
                        || !!state.program.categories.length,
                    complete: !!state.program.categories.find(
                        category => category.targetingSets.find(
                            set => set.name.length && set.creatives.length
                                && set.countries.length && set.draft === false,
                        ),
                    ),
                },
                summary: {
                    started: false,
                    complete: false,
                },
            }
        },
        countryByUuid: state => uuid => state.locations.countries.find(country => country.uuid === uuid),
        countryByCode: state => code => state.locations.countries.find(country => country.iso_alpha_2 === code),
        programHistory: state => state.programHistory,
    },
    mutations: {
        setError(state, error) {
            Vue.set(state.errors, error.name, error.value)
        },
        setLoadingState(state, loading) {
            Vue.set(state.loading, loading.name, loading.value)
        },
        setProgramList(state, programs) {
            Vue.set(state, 'programList', programs)
        },
        setProgramOwners(state, programOwners) {
            Vue.set(state, 'programOwners', programOwners)
        },
        setProgram(state, program) {
            const programPayload = { ...program.data, categories: [] }
            programPayload.max_budget_set = !!programPayload.max_monthly_budget

            let categoryUuids = programPayload.targeting_sets.map(set => set.category.uuid)
            categoryUuids = [ ...new Set(categoryUuids) ]
            categoryUuids.forEach(categoryUuid => {
                const categorySets = programPayload.targeting_sets.filter(set => set.category.uuid === categoryUuid)
                categorySets.map(set => {
                    set.draft = false;
                    set.expanded = false;
                    set.creatives = set.creatives.map(creative => creative.uuid);

                    return set;
                })
                programPayload.categories.push({
                    uuid: categoryUuid,
                    targetingSets: categorySets,
                })
            })

            programPayload.signal_targeting_set_uuids = programPayload.targeting_sets.map(({ uuid }) => uuid)

            programPayload.targeting_sets = []

            Vue.set(state, 'program', programPayload)
        },
        clearProgram(state) {
            Vue.set(state, 'program', {
                client_uuid: null,
                product_uuid: null,
                programowner_uuid: null,
                name: '',
                status: 'active',
                type: 'CPC',
                max_monthly_budget: null,
                notify_on_met_budget: false,
                email_weekly_report: true,
                categories: [],
                targeting_sets: [],
            })
        },
        clearProgramCategories(state) {
            Vue.set(state.program, 'categories', [])
        },
        updateProgramProperty(state, payload) {
            const { property, value } = payload
            state.program[property] = value
        },
        setProgramCategory(state, programCategory) {
            const match = state.program.categories.find(category => category.uuid === programCategory.uuid)
            if (match) {
                Object.assign(match, programCategory)
            } else {
                state.program.categories.push(programCategory)
            }
        },
        setGeoCountries(state, countries) {
            Vue.set(state, 'countries', countries)
        },
        setGeoLocations(state, locations) {
            function compare(a, b) {
                if (a.name < b.name) { return -1; }
                if (a.name > b.name) { return 1; }
                return 0;
            }
            Vue.set(state.locations, 'countries', locations.countries.sort(compare))
            Vue.set(state.locations, 'states', locations.states.sort(compare))
            Vue.set(state.locations, 'presets', locations.presets.sort(compare))
        },
        addProgram(state, program) {
            state.programList.push(program)
        },
        setTargetingSetHistory(state, history) {
            Vue.set(state, 'targetingSetHistory', history)
        },
        setProgramHistory(state, history) {
            Vue.set(state, 'programHistory', history)
        },
    },
    actions: {
        getProgram({ commit }, uuid) {
            commit('setLoadingState', { name: 'program', value: true })

            return programApi
                .getProgram(uuid)
                .then(response => {
                    const program = {
                        ...response.data.data,
                    }
                    commit('setProgram', program)
                })
                .catch(() => {
                    commit('setError', { name: 'program', value: true })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'program', value: false })
                })
        },
        updateProgram({ commit }, programPayload) {
            commit('setLoadingState', { name: 'programSave', value: true })
            commit('setError', { name: 'programSave', value: false })
            return programApi
                .updateProgram(programPayload.uuid, programPayload)
                .catch(errors => {
                    commit('setError', { name: 'programSave', value: true })

                    return errorPromise(errors, 'There was an error saving the campaign')
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'programSave', value: false })
                })
        },
        updateProgramStatus({ commit }, { programPayload, newStatus }) {
            const originalProgramStatus = programPayload.status
            let payload = {}
            if (newStatus === 'active') {
                // clear all paused type values
                payload = {
                    ...programPayload,
                    pause_reason: null,
                    paused_by: null,
                    paused_at: null,
                    status: 'active',
                }
            } else if (newStatus === 'inactive') {
                payload = {
                    ...programPayload,
                    status: 'inactive',
                }
            }
            commit('setLoadingState', { name: 'programStatusSave', value: true })
            commit('setError', { name: 'programStatusSave', value: false })
            return programApi
                .updateProgramStatus(programPayload.uuid, payload)
                .then(response => {
                    const properties = [ 'status', 'paused_at', 'paused_by', 'pause_reason' ];

                    properties.forEach(property => {
                        commit('updateProgramProperty', {
                            property,
                            value: response.data.data[property],
                        });
                    });
                })
                .catch(errors => {
                    commit('setError', { name: 'programStatusSave', value: true })

                    commit('updateProgramProperty', {
                        property: 'status',
                        value: originalProgramStatus,
                    })

                    return errorPromise(errors, 'There was an error saving the campaign status')
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'programStatusSave', value: false })
                })
        },
        addProgram({ commit }, programPayload) {
            commit('setLoadingState', { name: 'programSave', value: true })
            commit('setError', { name: 'programSave', value: false })

            return programApi
                .addProgram(programPayload)
                .then(response => {
                    const program = {
                        ...response.data.data,
                    }

                    commit('addProgram', program)
                    return program
                })
                .catch(errors => {
                    commit('setError', { name: 'programSave', value: true })

                    return errorPromise(errors, 'There was an error saving the campaign.')
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'programSave', value: false })
                })
        },
        setProgramList({ commit }, params = {}) {
            commit('setLoadingState', { name: 'programList', value: true })

            return programApi
                .getPrograms(params)
                .then(response => {
                    const { data } = response
                    commit('setProgramList', data.data)

                    return data.meta
                })
                .catch(() => {
                    commit('setError', {
                        name: 'programList',
                        value: true,
                    })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'programList', value: false })
                })
        },
        setProgramOwners({ commit }, params = {}) {
            commit('setLoadingState', { name: 'programOwners', value: true })

            programApi
                .getProgramOwners(params)
                .then(response => {
                    const { data } = response.data
                    commit('setProgramOwners', data)
                })
                .catch(() => {
                    commit('setError', {
                        name: 'programOwners',
                        value: true,
                    })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'programOwners', value: false })
                })
        },
        clearError({ commit }, name) {
            commit('setError', {
                name,
                value: null,
            })
        },
        initializeProgramManager({ dispatch }) {
            dispatch('setProgramList')
            dispatch('setProgramOwners')
        },
        initializeProgramOwners({ dispatch }, params = {}) {
            dispatch('setProgramOwners', params)
        },
        clearCurrentProgram({ commit }) {
            commit('clearProgram')
        },
        clearProgramCategories({ commit }) {
            commit('clearProgramCategories')
        },
        updateProgramProperty({ commit }, payload) {
            commit('updateProgramProperty', payload)
        },
        setProgramCategory({ commit }, payload) {
            commit('setProgramCategory', payload)
        },
        initializeCountries({ commit }) {
            return new Promise((resolve, reject) => {
                programApi.getGeoCountries()
                    .then(response => {
                        const countries = response.data.data
                        commit('setGeoCountries', countries)
                        resolve(response)
                    })
                    .catch(err => {
                        console.error(err)
                        reject(err)
                    })
            })
        },
        initializeTargeting({ commit }) {
            commit('setLoadingState', { name: 'locations', value: true })

            return programApi.getGeoLocations()
                .then(response => {
                    const { data } = response.data
                    commit('setGeoLocations', {
                        countries: data.countries,
                        states: data.states,
                        presets: data.presets,
                    })
                })
                .catch(err => {
                    console.error(err)
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'locations', value: false })
                })
        },
        getTargetingSetHistory({ commit }, uuid) {
            commit('setLoadingState', { name: 'targetingSetHistory', value: true })

            return programApi
                .getTargetingSetHistory(uuid)
                .then(response => {
                    const history = response.data.data
                    commit('setTargetingSetHistory', history)
                })
                .catch(() => {
                    commit('setError', { name: 'targetingSetHistory', value: true })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'targetingSetHistory', value: false })
                })
        },
        getProgramHistory({ commit }, uuid) {
            commit('setLoadingState', { name: 'programHistory', value: true })

            return programApi
                .getProgramHistory(uuid)
                .then(response => {
                    const history = response.data.data
                    commit('setProgramHistory', history)
                })
                .catch(() => {
                    commit('setError', { name: 'programHistory', value: true })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'programHistory', value: false })
                })
        },
    },
}
